// AgentChat — Floating chat panel overlay
// Renders on top of any page; delegates all chat UI to ChatInterface.
// Hidden automatically on /agent (the full-page Agent Dashboard).

import { useState } from "react";
import { useAgentContext } from "../../contexts/AgentContext";
import MemoryPanel from "./MemoryPanel";
import ChatInterface from "./ChatInterface";
import styles from "./AgentChat.module.css";
import { useModal } from "../../hooks/useModal";

interface AgentChatProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function AgentChat({ isOpen, onClose }: AgentChatProps) {
  const {
    unsentRequest,
    clearUnsentRequest,
    messages,
    sessions,
    currentSessionId,
    isConnected,
    isThinking,
    error,
    pendingPlan,
    sendMessage,
    newSession,
    selectSession,
    removeSession,
    approvePlan,
    rejectPlan,
    draftMessage,
    setDraftMessage,
  } = useAgentContext();

  const [showSessions, setShowSessions] = useState(false);
  const [showMemory, setShowMemory] = useState(false);
  const { modalRef } = useModal<HTMLDivElement>(isOpen, { onClose });

  if (!isOpen) return null;

  return (
    <>
      <div className={styles.backdrop} aria-hidden="true" onClick={onClose} />
      <div
        className={styles.panel}
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-label="AI Assistant"
        tabIndex={-1}
      >
        {/* Header */}
        <header className={styles.header}>
          <div className={styles.headerLeft}>
            <span
              className={`${styles.dot} ${isConnected ? styles.online : styles.offline}`}
            />
            <strong className={styles.title}>AI Assistant</strong>
          </div>
          <div className={styles.headerRight}>
            <button
              className={styles.iconBtn}
              onClick={() => {
                setShowMemory((s) => !s);
                if (showSessions) setShowSessions(false);
              }}
              title="What I remember about you"
            >
              🧠
            </button>
            <button
              className={styles.iconBtn}
              onClick={() => setShowSessions((s) => !s)}
              title="Sessions"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
            <button
              className={styles.iconBtn}
              onClick={onClose}
              aria-label="Close assistant"
              title="Close"
            >
              ✕
            </button>
          </div>
        </header>

        <MemoryPanel isOpen={showMemory} onClose={() => setShowMemory(false)} />

        {showSessions && (
          <div className={styles.sessionDrawer}>
            <button
              className={styles.newBtn}
              onClick={() => {
                newSession();
                setShowSessions(false);
              }}
            >
              + New Session
            </button>
            {sessions.map((s) => (
              <div
                key={s.session_id}
                className={`${styles.sessionRow} ${s.session_id === currentSessionId ? styles.sessionActive : ""}`}
                onClick={() => {
                  selectSession(s.session_id);
                  setShowSessions(false);
                }}
              >
                <span className={styles.sessionLabel}>
                  {s.title || s.session_id.slice(0, 8)}
                </span>
                <button
                  className={styles.delBtn}
                  onClick={(e) => {
                    e.stopPropagation();
                    removeSession(s.session_id);
                  }}
                >
                  ×
                </button>
              </div>
            ))}
            {sessions.length === 0 && (
              <p className={styles.hint}>No sessions yet</p>
            )}
          </div>
        )}

        <ChatInterface
          unsentRequest={unsentRequest}
          onClearUnsentRequest={clearUnsentRequest}
          conversationKey={currentSessionId || "new"}
          draft={{ value: draftMessage, onChange: setDraftMessage }}
          messages={messages}
          isThinking={isThinking}
          error={error}
          pendingPlan={pendingPlan}
          onSend={sendMessage}
          onApprove={approvePlan}
          onReject={rejectPlan}
        />
      </div>
    </>
  );
}
